<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="getAvailableItems(items)"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <div aria-live="polite" role="status">
      <div class="flex flex-col gap-16">
        <UnavailableCountry
          :country="addressList.deliver?.country || null"
          :shippable-countries
        />

        <ShippingAddress
          :address="addressList.deliver"
          :shippable-countries
          @updated="onUpdated"
          @updating="onUpdating"
        />

        <SameAsShippingCheckbox
          v-model="isSameAddressesCheckboxSelected"
          :addressList
        />

        <BillingAddress
          v-if="!isSameAddressesCheckboxSelected"
          :address="addressList.bill"
          :billable-countries
          @updated="onUpdated"
          @updating="onUpdating"
        />

        <UserInformations
          v-if="addressList.bill?.country"
          :country="addressList.bill.country"
        />
      </div>

      <Delivery
        :items
        :last-collection-point
        :show-error="hasCollectionPointError"
        @updated="onUpdated"
        @updating="onUpdating"
      />

      <div class="mt-56 flex justify-end">
        <RevButton
          ref="submit"
          data-qa="submit-button"
          :disabled="loading || isLoading"
          full-width="adaptive"
          :loading="loading || isLoading"
          type="submit"
          variant="primary"
          @click="onSubmit"
        >
          {{ i18n(translations.submitButton) }}
        </RevButton>
      </div>

      <GDPRNotice class="mt-24 !p-0 md:mt-56" page="delivery" />
      <ReassuranceItems
        class="mt-24 md:mt-56"
        :selected-mobile-plan="getSelectedMobilePlan(items)"
      />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import { navigateToPaymentPage } from '@backmarket/nuxt-layer-payment/pages/navigateToPaymentPage'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { pick } from '@backmarket/utils/object/pick'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevButton } from '@ds/components/Button'

import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import Delivery from '~/scopes/checkout/components/Delivery/Delivery.vue'
import GDPRNotice from '~/scopes/checkout/components/GDPRNotice/GDPRNotice.vue'
import ReassuranceItems from '~/scopes/checkout/components/ReassuranceItems/ReassuranceItems.vue'
import UnavailableCountry from '~/scopes/checkout/components/ShippingAddress/components/UnavailableCountry/UnavailableCountry.vue'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { useSaveBouyguesAddress } from '~/scopes/checkout/composables/useSaveBouyguesAddress'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { useUserInformationStore } from '~/scopes/checkout/stores/userInformationStore'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { getSelectedMobilePlan } from '~/scopes/checkout/utils/getSelectedMobilePlan'
import { hasCollectionPointAddressCompleted } from '~/scopes/checkout/utils/hasCollectionPointAddressCompleted'
import { hasCollectionPointOptionSelected } from '~/scopes/checkout/utils/hasCollectionPointOptionSelected'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import Stepper from '../../components/Stepper/Stepper.vue'

import translations from './AddressesStep.translations'
import BillingAddress from './components/BillingAddress/BillingAddress.vue'
import SameAsShippingCheckbox from './components/SameAsShippingCheckbox/SameAsShippingCheckbox.vue'
import ShippingAddress from './components/ShippingAddress/ShippingAddress.vue'
import UserInformations from './components/UserInformations/UserInformations.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', callback?: () => void): void
}>()

const i18n = useI18n()
const logger = useLogger()
const $httpFetch = useHttpFetch()

const { trackClick } = useTracking()
const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('ADDRESSES')
const { resetBuybackOffer } = useBuybackOffer()
const { saveBouyguesAddress } = useSaveBouyguesAddress()
const userInformationStore = useUserInformationStore()

const isSameAddressesCheckboxSelected = ref(false)
const isLoading = ref(false)
const hasCollectionPointError = ref(false)

onMounted(async () => {
  await redirectIfCartEmpty(props.items, CHECKOUT.CART)

  trackFunnel(props.items, props.swapOffer)
})

async function saveBillingAddress() {
  if (!isSameAddressesCheckboxSelected.value || !props.addressList.deliver) {
    return true
  }

  try {
    await $httpFetch('/bm/client/addresses/v2/billing', {
      method: 'POST',
      body: removeEmptyValuesInObject(
        pick(props.addressList.deliver, [
          'city',
          'company',
          'country',
          'firstName',
          'lastName',
          'postalCode',
          'stateOrProvince',
          'street',
          'street2',
        ]),
      ) as ApiSpecs['/bm/client/addresses/v2/billing']['POST']['body'],
    })

    return true
  } catch {
    throw new Error()
  }
}

async function saveUserInformations() {
  if (!userInformationStore.isFormRequired) {
    return true
  }

  try {
    await $httpFetch('/bm/cart/update-user-information', {
      method: 'POST',
      body: removeEmptyValuesInObject({
        formType: 'INSURANCE_ONLY_FORM',
        birthdate: userInformationStore.birthdate,
        taxId: userInformationStore.nationalId,
      }) as ApiSpecs['/bm/cart/update-user-information']['POST']['body'],
    })

    return true
  } catch {
    throw new Error()
  }
}

async function saveBuybackAddress() {
  if (!props.swapOffer || !props.addressList.deliver) {
    return true
  }

  try {
    await $httpFetch('/bm/buyback/v1/customer/address', {
      method: 'POST',
      body: removeEmptyValuesInObject(
        pick(props.addressList.deliver, [
          'city',
          'company',
          'country',
          'firstName',
          'lastName',
          'postalCode',
          'stateOrProvince',
          'street',
          'street2',
        ]),
      ),
    })

    return true
  } catch (err) {
    const error = err as Error
    logger.error('[BUYBACK][SWAP] Error while submitting customer address', {
      error,
      owners: ['bot-squad-checkout-front'],
    })

    await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
    resetBuybackOffer()

    return false
  }
}

async function saveBouyguesAddresses() {
  if (
    !hasSelectedMobilePlan(props.items) ||
    !props.addressList.deliver ||
    !props.addressList.bill
  ) {
    return true
  }

  try {
    await Promise.all([
      saveBouyguesAddress(props.addressList.deliver, 'SHIPPING'),
      saveBouyguesAddress(props.addressList.bill, 'BILLING'),
    ])

    return true
  } catch (err) {
    // TODO handle validation errors
    const error = err as Error
    logger.error('[BOUYGUES] Error while submitting customer address', {
      error,
      owners: ['bot-squad-checkout-front'],
    })

    throw new Error()
  }
}

async function validateCollectionPoint() {
  hasCollectionPointError.value = false

  if (
    hasCollectionPointOptionSelected(props.items) &&
    !hasCollectionPointAddressCompleted(props.lastCollectionPoint)
  ) {
    hasCollectionPointError.value = true

    const listing = props.items.find((item) =>
      item.options.some((option) =>
        option.choices.some(
          (choice) => choice.isCollectionPoint && choice.selected,
        ),
      ),
    )

    const shipping = (listing?.options[0].choices || []).find(
      (choice) => choice.isCollectionPoint,
    )

    const element = document.getElementById(
      `cart-shipping-option-${listing?.listingId}-${shipping?.shippingId}`,
    )
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })

    throw new Error()
  }
}

async function onSubmit() {
  isLoading.value = true

  try {
    trackClick({
      name: 'go_payment',
      zone: 'address',
    })

    await Promise.all([
      validateCollectionPoint(),
      saveBillingAddress(),
      saveBuybackAddress(),
      saveBouyguesAddresses(),
      saveUserInformations(),
    ])

    navigateToPaymentPage({ name: CHECKOUT.PAYMENT })
  } catch (e) {
    // TODO Show errors
  } finally {
    isLoading.value = false
  }
}

function onUpdating() {
  hasCollectionPointError.value = false
  isLoading.value = true
}

function onUpdated(cb?: () => void) {
  isLoading.value = false
  emit('update', cb)
}
</script>
